import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import './Company.css';

const CompanyEdit = ({ match }) => {
    const recordId = match.params.id;
    const history = useHistory();

    const [pinCodes, setPinCodes] = useState([]);
    const [itemdata, setItemData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [newRecordData, setNewRecordData] = useState({ FromAmount: '', ToAmount: '', PackingCharge: '' });

    const { isAuthenticated, companyId, isSuperAdmin, token, logout } = useAuth();

    const [formData, setFormData] = useState({
        Name: '', CompanyCode: '', ContactNumber1: '', ContactNumber2: '',
        Email: '', GSTNumber: '', AddressLine1: '', AddressLine2: '',
        PinCode: '', City: '', District: '', State: '', Country: '',
        BillRemarks: '', ReprintPassword: '', PaymentQRUPIId: '', PaymentQRUPIName: '',
    });

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchData('PinCodeApi', '/1', token)
            .then((r) => { if (r.responseCode === '1111') setPinCodes(r.response_data); })
            .catch((e) => console.error('Error fetching PIN codes:', e));
    }, [isAuthenticated, companyId, token]);

    useEffect(() => {
        fetchData('CompanyApi', '/CompanyDetails/' + recordId, token)
            .then((r) => { if (r.responseCode === '1111') setFormData(r.response_data); })
            .catch((e) => console.error('Error fetching Company Details:', e));
        fetchItemData();
    }, [recordId]);

    const fetchItemData = async () => {
        try {
            const response = await fetchData('CompanyApi', '/PackingDetails/' + recordId, token);
            if (response.responseCode === '1111') setItemData(response.response_data);
        } catch (e) { console.error('Error fetching Packing Details:', e); }
    };

    const handlePinCodeChange = (value) => {
        if (value.length === 6) {
            const p = pinCodes.find((pc) => pc.Postcode === value);
            if (p) setFormData((prev) => ({ ...prev, City: p.City, District: p.District, State: p.State, Country: p.Country }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateData('CompanyApi', '' + recordId, { ...formData, UpdatedBy: 1 }, token)
            .then(() => history.push('/companyIndex'))
            .catch((e) => console.error('Error updating record:', e));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewRecordData((prev) => ({ ...prev, [name]: value }));
    };

    const addRecord = async () => {
        if (!newRecordData.FromAmount || !newRecordData.ToAmount || !newRecordData.PackingCharge) {
            console.error('Please fill all required fields.');
            return;
        }
        try {
            await postData('PackingChargeApi', { ...newRecordData, CompanyId: recordId }, token);
            setNewRecordData({ FromAmount: '', ToAmount: '', PackingCharge: '' });
            fetchItemData();
            setIsModalOpen(false);
        } catch (e) { console.error('Add packing charge error:', e); }
    };

    const handleDelete = async () => {
        try {
            await deleteData('PackingChargeApi', '' + deleteItemId, token);
            fetchItemData();
            setDeleteItemId(null);
            setConfirmDialogOpen(false);
        } catch (e) { console.error('Error deleting record:', e); }
    };

    const openConfirmDialog = (id) => { setDeleteItemId(id); setConfirmDialogOpen(true); };
    const closeConfirmDialog = () => { setDeleteItemId(null); setConfirmDialogOpen(false); };

    return (
        <div className="company-container">
            <div className="company-header-card">
                <div className="company-header-content">
                    <div className="company-title-section">
                        <span className="company-title-icon"><BusinessIcon fontSize="small" /></span>
                        <h1 className="company-title">Company Edit</h1>
                    </div>
                    <div className="company-header-actions">
                        <button className="co-btn co-btn-update" onClick={handleSubmit}>
                            <SaveIcon style={{ fontSize: 14 }} />&nbsp;Update
                        </button>
                        <Link to="/companyIndex" className="co-btn co-btn-back">
                            <ArrowBackIcon style={{ fontSize: 14 }} />&nbsp;Back
                        </Link>
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ flex: '3', minWidth: '280px' }}>
                    <div className="company-form-card">
                        <p className="company-form-section-title">Basic Information</p>
                        <div className="company-form-row">
                            <div className="company-form-group w-3">
                                <label className="company-label">Company Name <span className="required">*</span></label>
                                <input className="company-input" name="Name" value={formData.Name} onChange={handleChange} type="text" placeholder="Enter company name" maxLength={50} />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">Company Code</label>
                                <input className="company-input" value={formData.CompanyCode} type="text" readOnly />
                            </div>
                        </div>
                        <div className="company-form-row">
                            <div className="company-form-group">
                                <label className="company-label">Contact Number 1 <span className="required">*</span></label>
                                <input className="company-input" name="ContactNumber1" value={formData.ContactNumber1} onChange={handleChange} type="text" placeholder="Enter contact number" maxLength={15} />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">Contact Number 2</label>
                                <input className="company-input" name="ContactNumber2" value={formData.ContactNumber2} onChange={handleChange} type="text" placeholder="Enter contact number" maxLength={15} />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">Email</label>
                                <input className="company-input" name="Email" value={formData.Email} onChange={handleChange} type="text" placeholder="Enter email" maxLength={30} />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">GST Number</label>
                                <input className="company-input" name="GSTNumber" value={formData.GSTNumber} onChange={handleChange} type="text" placeholder="Enter GST number" maxLength={15} />
                            </div>
                        </div>
                        <div className="company-form-row">
                            <div className="company-form-group">
                                <label className="company-label">Fssai Number</label>
                                <input className="company-input" name="BillRemarks" value={formData.BillRemarks} onChange={handleChange} type="text" placeholder="Enter Fssai number" maxLength={200} />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">KOT Reprint Password</label>
                                <input className="company-input" name="ReprintPassword" value={formData.ReprintPassword} onChange={handleChange} type="password" placeholder="Enter password" maxLength={10} />
                            </div>
                            {isSuperAdmin && (
                                <>
                                    <div className="company-form-group">
                                        <label className="company-label">Payment QR UPI Id</label>
                                        <input className="company-input" name="PaymentQRUPIId" value={formData.PaymentQRUPIId} onChange={handleChange} type="text" placeholder="Enter UPI Id" />
                                    </div>
                                    <div className="company-form-group">
                                        <label className="company-label">Payment QR UPI Name</label>
                                        <input className="company-input" name="PaymentQRUPIName" value={formData.PaymentQRUPIName} onChange={handleChange} type="text" placeholder="Enter UPI Name" />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="company-form-card">
                        <p className="company-form-section-title">Address</p>
                        <div className="company-form-row">
                            <div className="company-form-group w-2">
                                <label className="company-label">Address Line 1</label>
                                <input className="company-input" name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} type="text" placeholder="Enter address line 1" maxLength={200} />
                            </div>
                            <div className="company-form-group w-2">
                                <label className="company-label">Address Line 2</label>
                                <input className="company-input" name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} type="text" placeholder="Enter address line 2" maxLength={200} />
                            </div>
                        </div>
                        <div className="company-form-row">
                            <div className="company-form-group">
                                <label className="company-label">PIN Code</label>
                                <input className="company-input" name="PinCode" value={formData.PinCode} onChange={handleChange} type="text" placeholder="Enter PIN code" list="pinCodesList" onBlur={(e) => handlePinCodeChange(e.target.value)} />
                                <datalist id="pinCodesList">
                                    {pinCodes.map((p) => <option key={p.Id} value={p.Postcode} />)}
                                </datalist>
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">City</label>
                                <input className="company-input" name="City" value={formData.City} onChange={handleChange} type="text" readOnly />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">District</label>
                                <input className="company-input" name="District" value={formData.District} onChange={handleChange} type="text" readOnly />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">State</label>
                                <input className="company-input" name="State" value={formData.State} onChange={handleChange} type="text" readOnly />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">Country</label>
                                <input className="company-input" name="Country" value={formData.Country} onChange={handleChange} type="text" readOnly />
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ flex: '2', minWidth: '260px' }}>
                    <div className="company-form-card">
                        <div className="packing-section-header">
                            <p className="packing-section-title">Packing Charge Details</p>
                            <button className="co-btn co-btn-add" onClick={() => setIsModalOpen(true)}>
                                <AddIcon style={{ fontSize: 13 }} />&nbsp;Add
                            </button>
                        </div>

                        <div className="company-table-wrapper" style={{ marginBottom: 0 }}>
                            <div className="company-table-container" style={{ maxHeight: '300px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th className="company-th" style={{ padding: '6px 10px', textAlign: 'center' }}>From</th>
                                            <th className="company-th" style={{ padding: '6px 10px', textAlign: 'center' }}>To</th>
                                            <th className="company-th" style={{ padding: '6px 10px', textAlign: 'center' }}>Charge</th>
                                            <th className="company-th" style={{ padding: '6px 10px', textAlign: 'center' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itemdata.map((row, index) => (
                                            <tr key={index} className="company-row">
                                                <td className="company-td" style={{ textAlign: 'center' }}>{row.FromAmount}</td>
                                                <td className="company-td" style={{ textAlign: 'center' }}>{row.ToAmount}</td>
                                                <td className="company-td" style={{ textAlign: 'center' }}>{row.PackingCharge}</td>
                                                <td className="company-td" style={{ textAlign: 'center' }}>
                                                    <IconButton onClick={() => openConfirmDialog(row.Id)} className="company-icon-btn company-icon-delete" size="small">
                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="company-modal-overlay">
                    <div className="company-modal">
                        <p className="company-modal-title">Add Packing Charge</p>
                        <div className="company-form-row">
                            <div className="company-form-group">
                                <label className="company-label">From Amount</label>
                                <input className="company-input" name="FromAmount" value={newRecordData.FromAmount} onChange={handleInputChange} type="text" placeholder="0.00" />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">To Amount</label>
                                <input className="company-input" name="ToAmount" value={newRecordData.ToAmount} onChange={handleInputChange} type="text" placeholder="0.00" />
                            </div>
                            <div className="company-form-group">
                                <label className="company-label">Packing Charge</label>
                                <input className="company-input" name="PackingCharge" value={newRecordData.PackingCharge} onChange={handleInputChange} type="text" placeholder="0.00" />
                            </div>
                        </div>
                        <div className="company-modal-actions">
                            <button className="co-btn co-btn-confirm" onClick={addRecord}>Add</button>
                            <button className="co-btn co-btn-cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog} PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}>
                <DialogTitle style={{ fontSize: '16px', fontWeight: 600 }}>Delete Record?</DialogTitle>
                <DialogContent>
                    <DialogContentText style={{ fontSize: '13px' }}>Are you sure you want to delete this record?</DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
                    <button onClick={closeConfirmDialog} className="company-dialog-btn co-btn co-btn-cancel">Cancel</button>
                    <button onClick={handleDelete} className="company-dialog-btn co-btn co-btn-back">Delete</button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CompanyEdit;
